import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  Textarea,
  useRadio,
  useRadioGroup,
  useToast,
  VStack,
} from '@chakra-ui/react';
import axios from 'axios';
import Select from 'react-select';
import Tippy from '@tippyjs/react';
import { useTranslation } from 'react-i18next';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';

import Plugin, { PluginProps } from '../Plugin';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import getStudents from '../../../helpers/getStudents';
import { trackAction } from '../../../../../helpers/trackAction';
import { fullNameUsernameOrEmail } from '../../../../../helpers/username';
import timeAgo from '../../../../../helpers/timeAgo';
import { useQueryParam } from '../../../../../hooks/useQueryParams';
import { PluginType } from '../../../types/JsonCmsContent';
import TriggerBlock from './TriggerBlock';
import Markdown from '../../../../../components/Markdown';
import { useIsMobile } from '../../../../../hooks/isMobile';
import { CHALLENGE_VIRTUAL_STUDY_GROUP_ID } from './FileBlock';
import useTeacherStudyGroup from '../../../hooks/useTeacherStudyGroup';
import useInteractiveQuizAnswersFromActiveLicenses from '../../../hooks/useInteractiveQuizAnswersFromActiveLicenses';
import AdminInteractiveQuizAnswers from './AdminInteractiveQuizAnswers';

type StudentAnswerProps = {
  isCorrect: boolean;
  manuallyGraded: boolean;
  points: number;
  maxPoints: number;
  date: Date;
  student: any;
  answers: any;
  allAnswers: { answer: string; isCorrect: boolean }[];
  questionType: string;
  getOldAnswers: () => void;
  questionId: string;
  studyGroupId: string;
  studentId: string;
  answerId: string;
};

const StudentAnswer: React.FC<StudentAnswerProps> = (props) => {
  const {
    isCorrect,
    points,
    maxPoints,
    date,
    student,
    answers,
    allAnswers,
    manuallyGraded,
    questionType,
    getOldAnswers,
    questionId,
    studyGroupId,
    studentId,
    answerId,
  } = props;
  const { t } = useTranslation();
  const [gradePoints, setGradePoints] = useState<number>(points);
  const toast = useToast();

  let isString = typeof answers == 'string';

  const needsGrading = !manuallyGraded && questionType === 'FreeForm';

  const gradeAnswer = () => {
    axios
      .patch(
        '/api/v1/syllabus/interactive_quiz_answers/',
        {
          question_id: questionId,
          study_group_id: studyGroupId,
          student_id: studentId,
          answer_id: answerId,
          points: gradePoints,
        },
        {
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        },
      )
      .then((response) => {
        toast({
          title: t('plugin.interactiveQuiz.success'),
          description: t('plugin.interactiveQuiz.gradedAnswer'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        getOldAnswers();
      })
      .catch((error) => {
        console.log(error)
        toast({
          title: t('plugin.interactiveQuiz.error'),
          description: error,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        console.error(error);
      });
  };

  return (
    <VStack
      p={2}
      // backgroundColor={
      //   isCorrect
      //     ? colors.applyOpacity(colors.limegreen, 0.1)
      //     : colors.applyOpacity(colors.crimson, 0.1)
      // }
      borderRadius="2xl"
      justify="start"
      align="start"
      spacing={0}
      gap={1}
    >
      <HStack
        w="full"
        justify="start"
        spacing={0}
        gap={4}
        justifyContent="space-between"
      >
        <Text
          minW="450px"
          as="div"
          p={0}
          m={0}
          display="flex"
          alignItems="center"
        >
          {fullNameUsernameOrEmail(student)}
          &nbsp;
          {needsGrading ? (
            <Text as="span" color="#4e06b3" fontSize="14px">
              {t('plugin.interactiveQuiz.notGradedYet')}
            </Text>
          ) : (
            <>
              {isCorrect ? '✅' : '❌'}
              &nbsp;
              <Text
                as="span"
                fontSize="14px"
              >{`(${points} / ${maxPoints})`}</Text>
            </>
          )}
        </Text>
        <Tippy
          content={`${date.toLocaleDateString()} ${date.toLocaleTimeString()}`}
        >
          <Text as="div">{timeAgo(date)}</Text>
        </Tippy>
      </HStack>
      <HStack
        w="full"
        justifyContent="space-between"
        alignItems="center"
        spacing={0}
        gap={4}
      >
        <HStack align="start" justify="start" spacing={0} gap={1}>
          {isString ? (
            <Text w="full" boxShadow="md" py={2} px={4}>
              {answers}
            </Text>
          ) : (
            Object.entries(allAnswers).map(([key, value]: any, index) => {
              const nkey = Number(key);
              const wasSelected = answers.includes(nkey);
              let isUserCorrect = null;
              if (wasSelected && value.isCorrect) {
                isUserCorrect = true;
              } else if (wasSelected && !value.isCorrect) {
                isUserCorrect = false;
              } else if (!wasSelected && value.isCorrect) {
                isUserCorrect = false;
              }
              return (
                <Box
                  borderWidth="2px"
                  borderRadius="md"
                  boxShadow="md"
                  px={5}
                  py={3}
                  key={index}
                  style={{
                    ...(isUserCorrect === null
                      ? {}
                      : isUserCorrect
                        ? {
                          background: 'rgba(117,214,64,1)',
                          color: 'white',
                          borderColor: 'rgba(117,214,64,1)',
                          fontWeight: '600',
                        }
                        : wasSelected
                          ? {
                            background: 'rgba(220,53,69,1)',
                            color: 'white',
                            borderColor: 'rgba(220,53,69,1)',
                            fontWeight: '600',
                            borderWidth: '2px',
                          }
                          : {
                            background: 'white',
                            color: 'black',
                            borderColor: 'rgba(117,214,64,1)',
                            fontWeight: '600',
                            borderWidth: '2px',
                          }),
                  }}
                >
                  {value.answer}
                </Box>
              );
            })
          )}
        </HStack>
        <HStack
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          gap={4}
        >
          <NumberInput
            allowMouseWheel
            maxWidth="80px"
            defaultValue={points}
            min={-1}
            max={maxPoints}
            onChange={(val) => setGradePoints(Number(val))}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Text as="span">/ {maxPoints}</Text>
          <Button colorScheme="blue" variant="solid" onClick={gradeAnswer}>
            {t('plugin.interactiveQuiz.grade')}
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
};

const Answer = (props: any) => {
  const { getInputProps, getCheckboxProps } = useRadio(props);

  const input = getInputProps();
  const checkbox = getCheckboxProps();

  return (
    <Box as="label" onClick={() => { }}>
      <input {...input} onClick={() => { }} />
      <Box
        {...checkbox}
        cursor="pointer"
        borderWidth="1px"
        borderRadius="md"
        boxShadow="md"
        _checked={{
          bg: 'blue.600',
          color: 'white',
          borderColor: 'blue.600',
          fontWeight: '600',
        }}
        _focus={{
          boxShadow: 'outline',
        }}
        px={5}
        py={3}
        onClick={props.onClick}
        style={{
          ...(props.isUserCorrect === null
            ? {}
            : props.isUserCorrect
              ? {
                background: 'rgba(117,214,64,1)',
                color: 'white',
                borderColor: 'rgba(117,214,64,1)',
                fontWeight: '600',
              }
              : props.wasSelected
                ? {
                  background: 'rgba(220,53,69,1)',
                  color: 'white',
                  borderColor: 'rgba(220,53,69,1)',
                  fontWeight: '600',
                  borderWidth: 4,
                }
                : {
                  background: 'white',
                  color: 'black',
                  borderColor: 'rgba(117,214,64,1)',
                  fontWeight: '600',
                  borderWidth: 4,
                }),
        }}
      >
        {props.children}
      </Box>
    </Box>
  );
};

const InteractiveQuiz = (props: PluginProps) => {
  const { content, studyGroup } = props;
  const {
    students: studentsFromTeacherStudyGroups,
    teacherStudyGroups,
  } = useTeacherStudyGroup();
  const { t } = useTranslation();
  const qStudentId = useQueryParam('student-id');
  const qQuizId = useQueryParam('quiz-id');
  const contentData = content.data;
  const questionType = contentData.question_type;
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);
  const [disableAnswer, setDisableAnswer] = useState(false);
  const user = useContext(AuthContext);
  const [textareaValue, setTextareaValue] = useState('');
  const [textareaDisabled, setTextareaDisabled] = useState(false);
  const [studentId, setStudentId] = useState<string | null>(qStudentId);
  const [students, setStudents] = useState<any[]>([]);
  const [studentAnswers, setStudentAnswers] = useState<any>([]);
  const isTeacher = user?.roles?.includes(Role.teacher);
  const isStaff = user?.roles?.includes(Role.staff);
  const [showStudentAnswers, setShowStudentAnswers] = useState(
    (isTeacher) && qQuizId === String(contentData.id),
  );
  const [showAllSchools, setShowAllSchools] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();
  const isMobile = useIsMobile();
  const currentQuestionId = contentData.unique_quiz_id;
  const isMulti = contentData.choice_type === 'MultiChoice';
  const repeatCount = contentData.repeat_count;
  const correctAnswers: any[] = [];
  const asnwerMap: Record<string, { answer: string; is_correct: boolean }> = {};
  Object.entries(contentData.answers).forEach(([key, value]: any) => {
    if (value.isCorrect) {
      correctAnswers.push(key);
    }
    asnwerMap[key] = value;
  });

  const { quizAnswers } = useInteractiveQuizAnswersFromActiveLicenses(currentQuestionId)

  const INCORRECT_ANSWER_RENDER = (
    <Text display="inline" color="rgba(220,53,69,1)">
      {t('plugin.interactiveQuiz.incorrect')}
    </Text>
  );

  const PARTIALLY_CORRECT_ANSWER_RENDER = (
    <Text display="inline" color="rgba(255,193,7,1)">
      {t('plugin.interactiveQuiz.partiallyCorrect')}
    </Text>
  );

  const CORRECT_ANSWER_RENDER = (
    <Text display="inline" color="rgba(117,214,64,1)">
      {t('plugin.interactiveQuiz.correct')}
    </Text>
  );

  const checkStudentAnswers = (
    studentAnswers: {
      model: string;
      pk: number;
      fields: {
        answer_id: string;
        student_id: string;
        study_group_id: string;
        question_id: string | null;
        unique_quiz_id: string;
        answers: string;
        is_correct: boolean;
        points: number;
        manually_graded: boolean;
        created_at: string;
        ai_grade: string;
      };
    },
    allAnswers: Record<string, { answer: string; isCorrect: boolean }>,
  ) => {
    const userAnswers = studentAnswers.fields.answers.split(',');
    const allAnswersKeys = Object.keys(allAnswers);

    const correctAnswersKeys = Object.keys(allAnswers).filter(
      (key) => allAnswers[key]?.isCorrect,
    );
    const incorrectAnswersKeys = Object.keys(allAnswers).filter(
      (key) => !allAnswers[key]?.isCorrect,
    );

    // if user has incorrect answer
    if (
      userAnswers.some((answer: string) =>
        incorrectAnswersKeys.includes(answer),
      )
    ) {
      return INCORRECT_ANSWER_RENDER;
    }

    // if user checked all answers to trick quiz and not all answers are correct
    if (
      allAnswersKeys.length !== correctAnswersKeys.length &&
      userAnswers.length === allAnswersKeys.length
    ) {
      return INCORRECT_ANSWER_RENDER;
    }

    // if user answers are same as correct answers
    if (
      userAnswers.length === correctAnswersKeys.length &&
      correctAnswersKeys.every((key) => userAnswers.includes(key))
    ) {
      return CORRECT_ANSWER_RENDER;
    }

    return PARTIALLY_CORRECT_ANSWER_RENDER;
  };

  const studentOptions = React.useMemo(() => {
    return students.map((s) => ({
      value: s.pk,
      label: fullNameUsernameOrEmail(s),
    }));
  }, [students]);

  const userAnswers = studentAnswers.filter((answer: any) => {
    return answer.fields.student_id === user?.pk;
  });

  const saveAnswer = () => {
    let isUserCorrect = true;
    let correctCount = 0;
    correctAnswers.forEach((correctAnswer) => {
      if (!selectedAnswers.includes(correctAnswer)) {
        isUserCorrect = false;
      } else {
        correctCount += 1;
      }
    });
    if (selectedAnswers.length !== correctAnswers.length) {
      isUserCorrect = false;
    }
    const userPercentage =
      correctCount / Math.max(correctAnswers.length, selectedAnswers.length);
    const userAnswer: {
      isUserCorrect: boolean;
      selectedAnswers: number[];
    } = {
      isUserCorrect,
      selectedAnswers,
    };
    trackAction({
      action_type: 'answered_quiz',
      action_object: {
        type: 'content',
        pk: contentData.pk,
      },
      study_group: { pk: studyGroup.pk },
      data: {
        questionId: currentQuestionId,
        correct: userAnswer.isUserCorrect,
        answers:
          questionType === 'FreeForm'
            ? textareaValue
            : userAnswer.selectedAnswers.join
              ? userAnswer.selectedAnswers.join(',')
              : userAnswer.selectedAnswers,
      },
    });
    setIsSaving(true);
    axios
      .post(
        '/api/v1/syllabus/interactive_quiz_answers/',
        {
          question_id: currentQuestionId,
          study_group_id: studyGroup.pk || CHALLENGE_VIRTUAL_STUDY_GROUP_ID,
          answers:
            questionType === 'FreeForm'
              ? textareaValue
              : userAnswer.selectedAnswers.join
                ? userAnswer.selectedAnswers.join(',')
                : userAnswer.selectedAnswers,
        },
        {
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        },
      )
      .then((response) => {
        if (questionType === 'FreeForm') {
          toast({
            title: t('plugin.interactiveQuiz.success'),
            description: t('plugin.interactiveQuiz.gradedAnswer'),
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
        getOldAnswers();
      })
      .catch((error) => {
        console.error(error);
        toast({
          title: t('plugin.interactiveQuiz.errorSavingAnswer'),
          description: error,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      })
      .finally(() => {
        setShowCorrectAnswers(true);
        setIsSaving(false);
      });
  };

  const getOldAnswers = () => {
    if (isTeacher) {
      axios
        .get('/api/v1/syllabus/interactive_quiz_answers/', {
          params: {
            study_group_id: studyGroup.pk || CHALLENGE_VIRTUAL_STUDY_GROUP_ID,
            question_id: currentQuestionId,
            get_study_group_data: true,
          },
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        })
        .then((response) => {
          setStudentAnswers(JSON.parse(response.data));
        })
        .catch((e) => console.error(e));
    } else {
      axios
        .get('/api/v1/syllabus/interactive_quiz_answers/', {
          params: {
            study_group_id: studyGroup.pk || CHALLENGE_VIRTUAL_STUDY_GROUP_ID,
            question_id: currentQuestionId,
          },
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        })
        .then((response) => {
          const oldAnswersData = JSON.parse(response.data);
          setStudentAnswers(oldAnswersData);
          if (oldAnswersData.length >= repeatCount && repeatCount !== -1) {
            // Block answer
            setDisableAnswer(true);
            setShowCorrectAnswers(true);
            setTextareaDisabled(true);
          } else {
            // Add event listener
            if (repeatCount !== -1 && oldAnswersData.length > 0) {
            }
          }
          if (questionType === 'FreeForm') {
            if (oldAnswersData.length) {
              setTextareaValue(
                oldAnswersData[oldAnswersData.length - 1].fields.answers,
              );
            }
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  useEffect(() => {
    if (isStaff) {
      // we are handling staff differently - showing all students
      return
    }

    if (isTeacher) {
      if (studyGroup?.pk) {
        getStudents(studyGroup.pk).then(setStudents);
      } else if (studentsFromTeacherStudyGroups?.length) {
        setStudents(studentsFromTeacherStudyGroups);
      }
    }

    if (studyGroup || teacherStudyGroups?.length) {
      getOldAnswers();
    }

    return () => {
      setStudentAnswers([]);
    }
  }, [studyGroup, studentsFromTeacherStudyGroups]);

  useEffect(() => {
    if (!showStudentAnswers) {
      setShowStudentAnswers(
        (isTeacher || isStaff) && qQuizId === String(contentData.unique_quiz_id),
      );
      setTimeout(() => {
        const el = document.querySelector(
          `.question-${qQuizId}`,
        ) as HTMLElement;
        if (el) {
          const offset =
            (document.querySelector('.content-controls') as HTMLElement)
              ?.clientHeight || 0;
          window.scrollTo({ top: el.offsetTop - offset, behavior: 'smooth' });
        }
      }, 400);
    }
  }, [user, contentData]);

  const onChange = (v: any) => {
    if (disableAnswer) {
      return;
    }
    if (isMulti) {
      if (selectedAnswers.includes(v)) {
        setSelectedAnswers((s) => {
          s.splice(s.indexOf(v), 1);
          return [...s];
        });
      } else {
        setSelectedAnswers((s) => {
          s.push(v);
          return [...s];
        });
      }
    } else {
      setSelectedAnswers([v]);
    }
  };

  const handleInputChange = (e: any) => {
    setTextareaValue(e.target.value);
  };

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'answer',
    defaultValue: '',
    onChange: () => { },
  });

  const showAnswerButton =
    !disableAnswer &&
    !showStudentAnswers &&
    ((questionType === 'ABCDQuestion' && !!selectedAnswers.length) ||
      (questionType === 'FreeForm' && textareaValue));

  const getRealABCDEAnswers = (answer: any) => {
    const answers = answer
      .split(',')
      .map((a: string) => asnwerMap[a.trim()]?.answer)
      .join(', ');
    return answers;
  };

  const lastStudentAnswer = studentAnswers.at(-1);
  let aiGrade = {
    points: -1,
    suggestion: '',
  };
  if (lastStudentAnswer) {
    try {
      let aiGradeParsed = JSON.parse(
        lastStudentAnswer?.fields?.ai_grade?.replaceAll("'", '"'),
      );
      if (aiGradeParsed && aiGradeParsed.response) {
        aiGradeParsed = aiGradeParsed.response;
      }
      if (aiGradeParsed.points && aiGradeParsed.suggestion) {
        aiGrade = aiGradeParsed;
      }
    } catch (e) {
      console.log(e);
      aiGrade.suggestion = lastStudentAnswer?.fields?.ai_grade;
    }
  }

  return (
    <Box
      paddingTop={8}
      fontSize={isMobile ? 'sm' : 'xl'}
      className={`question-${contentData.unique_quiz_id}`}
    >
      <Text
        fontWeight="medium"
        dangerouslySetInnerHTML={{ __html: contentData.question }}
      ></Text>
      <Text fontSize="small" fontWeight="bold">
        ({contentData.max_points} pts.)
      </Text>
      {(isTeacher) && (
        <HStack spacing={0} justify="space-between">
          <Box>
            <Button
              mb="4"
              variant="ghost"
              color="blue"
              onClick={() => {
                setShowStudentAnswers(!showStudentAnswers);
              }}
            >
              {showStudentAnswers
                ? t('plugin.interactiveQuiz.hideStudentAnswers')
                : t('plugin.interactiveQuiz.showStudentAnswers')}
            </Button>
            <Button
              mb="4"
              variant="ghost"
              color="blue"
              onClick={() => {
                setShowAllSchools(!showAllSchools);
              }}
            >
              {showAllSchools
                ? t('plugin.interactiveQuiz.hideAllSchools')
                : t('plugin.interactiveQuiz.showAllSchools')}
            </Button>
            {showStudentAnswers && (
              <Select
                styles={{
                  container: (provided, state) => ({
                    ...provided,
                    minWidth: '350px',
                    textAlign: 'left',
                    // marginRight: '40% !important',
                    marginBottom: '1rem',
                    marginLeft: '1rem'
                  }),
                }}
                isClearable
                placeholder={t('plugin.interactiveQuiz.selectStudent')}
                noOptionsMessage={() => t('plugin.interactiveQuiz.noStudents')}
                options={studentOptions}
                value={studentOptions.find((o) => o.value === studentId)}
                onChange={(value) => {
                  if (value) setStudentId(value.value);
                  else setStudentId(null);
                }}
                menuPlacement="auto" // ticket ENG-699
                menuPortalTarget={document.body}
              />
            )}
          </Box>
        </HStack>
      )}

      {showStudentAnswers && (
        <VStack
          w="full"
          justifyContent="start"
          align="start"
          spacing={0}
          gap={4}
        >
          {studentAnswers.map(
            ({ fields: studentAnswer }: any, index: number) => {
              const student = students.find(
                (s) => s.pk === studentAnswer.student_id,
              );
              if (
                studentId != null &&
                student != null &&
                student.pk !== studentId
              ) {
                return;
              }
              if (!student) {
                return;
              }

              let answers =
                contentData.question_type === 'ABCDQuestion'
                  ? studentAnswer.answers.split(',').map((key: string) => {
                    const nkey = Number(key);
                    return nkey;
                  })
                  : studentAnswer.answers;
              return (
                <StudentAnswer
                  key={index}
                  isCorrect={studentAnswer.is_correct}
                  student={student}
                  answers={answers}
                  allAnswers={contentData.answers}
                  points={studentAnswer.points}
                  maxPoints={contentData.max_points}
                  date={new Date(studentAnswer.created_at)}
                  questionType={contentData.question_type}
                  manuallyGraded={studentAnswer.manually_graded}
                  getOldAnswers={getOldAnswers}
                  questionId={contentData.unique_quiz_id}
                  studentId={studentAnswer.student_id}
                  studyGroupId={studyGroup.pk}
                  answerId={studentAnswer.answer_id}
                />
              );
            },
          )}
        </VStack>
      )}
      {showStudentAnswers && studentAnswers.length === 0 && (
        <Text>{t('plugin.interactiveQuiz.noStudentAnswers')}</Text>
      )}

      {questionType === 'ABCDQuestion' && userAnswers.length ? (
        <>
          <Text fontWeight="bold">
            {t('plugin.interactiveQuiz.attempt')} {userAnswers.length}
            {repeatCount !== -1
              ? `${t('plugin.interactiveQuiz.of')} ${repeatCount}`
              : ''}
            <br />
            {t('plugin.interactiveQuiz.lastAnswer')} '
            {getRealABCDEAnswers(
              userAnswers[userAnswers.length - 1].fields.answers,
            )}
            ' {t('plugin.interactiveQuiz.andItWas')}
            {checkStudentAnswers(
              studentAnswers[studentAnswers.length - 1],
              contentData.answers,
            )}
          </Text>
        </>
      ) : null}

      {questionType === 'FreeForm' &&
        isStaff && (!studyGroup?.pk || showAllSchools) && (
          <Box mt={8}>
            <AdminInteractiveQuizAnswers
              unique_quiz_id={contentData.unique_quiz_id}
              question_type={contentData.question_type}
              answers={contentData.answers}
              max_points={contentData.max_points}
              question_id={currentQuestionId}
            />
          </Box>)
      }

      {questionType === 'ABCDQuestion'
        ? !showStudentAnswers && (
          <VStack key={showCorrectAnswers ? '1' : '0'} alignItems="normal">
            {Object.entries(contentData.answers).map(([key, value]: any) => {
              const radio = getRadioProps({ value: key });
              let isUserCorrect = null;
              let wasSelected = null;
              if (
                showCorrectAnswers &&
                studentAnswers[studentAnswers.length - 1]
              ) {
                wasSelected = studentAnswers[
                  studentAnswers.length - 1
                ].fields.answers
                  .split(',')
                  .includes(key);
                if (wasSelected && contentData.answers[key].isCorrect) {
                  isUserCorrect = true;
                } else if (
                  wasSelected &&
                  !contentData.answers[key].isCorrect
                ) {
                  isUserCorrect = false;
                } else if (
                  !wasSelected &&
                  contentData.answers[key].isCorrect
                ) {
                  isUserCorrect = false;
                }
              }
              return (
                <Answer
                  key={key}
                  {...radio}
                  isChecked={selectedAnswers.includes(key)}
                  onClick={() => onChange(key)}
                  isCorrect={value.isCorrect}
                  isUserCorrect={isUserCorrect}
                  wasSelected={wasSelected}
                >
                  {value.answer}
                </Answer>
              );
            })}
            {isStaff && (!studyGroup?.pk || showAllSchools) && (
              <Box mt={8}>
                <AdminInteractiveQuizAnswers
                  unique_quiz_id={contentData.unique_quiz_id}
                  question_type={contentData.question_type}
                  answers={contentData.answers}
                  max_points={contentData.max_points}
                  question_id={currentQuestionId}
                />
              </Box>
            )}
          </VStack>
        )
        : !showStudentAnswers && (
          <div>
            <Textarea
              value={textareaValue}
              onChange={handleInputChange}
              placeholder={t('plugin.interactiveQuiz.writeYourAnswerHere')}
              isReadOnly={textareaDisabled}
            />
            {aiGrade.suggestion && (
              <div>
                {aiGrade.points >= 0 && `${aiGrade.points}/100`}
                <Markdown markdown={aiGrade.suggestion} />
              </div>
            )}
          </div>
        )}
      {showAnswerButton && (
        <Button
          colorScheme="blue"
          mt={6}
          onClick={saveAnswer}
          isLoading={isSaving}
          disabled={userAnswers.length >= repeatCount && repeatCount !== -1}
        >
          {t('general.answer')}
        </Button>
      )}
      {contentData.use_ai_help &&
        content.children.map((child, index) => {
          if (index > 0) {
            return null;
          }
          if (child.plugin === PluginType.TriggerBlockPlugin) {
            return <TriggerBlock key={index} {...props} content={child} />;
          }
          return null;
        })}
    </Box>
  );
};

export default InteractiveQuiz;
